import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import Banner from '@cpg-its/ads.containers.banner';
import { Form, Formik, FormikProps } from 'formik';
import { DividerSection } from '../../../global/components/layout/divider-section';
import { TcsmStates } from '../../../root/tcsm-model';
import { ClergyBioSettings } from '../../arc/models/arc-epi-props';
import { ClergyBioChurchMinistries, DataState } from '../model/clergy-bio-app-models';
import ClergyBioComponentLoader from '../components/clergy-bio-component-loader';
import { LabelSettingProps } from '../../../global/models/epi-model';
import { ApiStatuses } from '../../../global/models/api-model';
import { useApiResponseError } from '../../../global/hooks/useApiError';
import { setClergyBioMinistries, setLoadingState } from '../clergy-bio-slice';
import { getClergyBioMinistry, patchClergyBioMinistry } from '../../../global/ducks/clergy-bio-api';
import { FormWrapper } from '../../../global/components/form-wrapper';
import { FormTypes } from '../../account-info/models/form-models';
import EcdMessage from '../summary/ecd-message';
import { ContentSpacer } from '../../../global/components/layout/generic-content-spacer';
import { ClergyBioMinistriesFormModel } from './models/form-models';
import { ConstructPayload, createNewMinistry, createNewMiscellaneous, createNewPosition, GetClergyBioMinistriesValidationSchema } from './clergy-bio-ministries-helpers';
import ClergyBioMinistryItem from './clergy-bio-ministry-item';
import ClergyBioMiscellaneousItem from './clergy-bio-miscellaneous-item';
import { PageHeader } from '../../../global/components/layout/page-header';
import { buttonStyles } from '../../../global/constants/theme/styles/components/buttonstyles';
import { ClergyBioGaFormTypes, HandleClergyBioSubmit } from '../../../global/helpers/analytics-helper';
import { WysiwygWithReactLink } from '../../../global/components/wysiwyg-with-react-link';




function ClergyBioMinistriesUpdate() {

	const buttonClasses = buttonStyles();
	const settings = useSelector<TcsmStates, ClergyBioSettings>(state => state?.epiProps?.clergyBioSettingsState?.epiContent);
	const loadingState = useSelector<TcsmStates, DataState>(state => state?.clergyBioState?.loaded?.ministries);
	const ministries = useSelector<TcsmStates, ClergyBioChurchMinistries>(state => state?.clergyBioState?.ministries);
	const guid = useSelector<TcsmStates, string>(state => state?.authenticatedState?.clientGuid);
	const labelSettings = useSelector<TcsmStates, LabelSettingProps>(state => state?.epiProps?.labelSettingState?.epiContent);
	const dispatch = useDispatch();
	const [updateStatus, setUpdateStatus] = useState<ApiStatuses>(ApiStatuses.None);
	const [errorMessage, setErrorMessage] = useState<string>(null);
	const defaultValues = {
		stipendiary: _.cloneDeep(ministries?.stipendiary?.details),
		miscellaneous: _.cloneDeep(ministries?.miscellaneous?.details),
		nonStipendiary: _.cloneDeep(ministries?.nonStipendiary?.details),
	};
	const [initialValues, setInitialValues] = useState<ClergyBioMinistriesFormModel>(
		{
			stipendiary: [],
			miscellaneous: [],
			nonStipendiary: [],
		},
	);
	const apiErrors = useApiResponseError();

	const onSuccess = (res) => {
		if (res?.status?.code === 'SUCCESS') {
			HandleClergyBioSubmit(ClergyBioGaFormTypes.Ministry, guid);
			setUpdateStatus(ApiStatuses.Success);
		}
		if (res?.status?.code === 'ERROR') {
			setUpdateStatus(ApiStatuses.Failure);
			setErrorMessage(res?.status?.message);
		}
		dispatch(setClergyBioMinistries(res?.data));
	};



	const updateMinistries = (values: ClergyBioMinistriesFormModel) => {
		let scrollTo = '';

		const emptyStip = values?.stipendiary?.filter(
			(a, idx) => {
				if (!scrollTo && a?.positions?.length === 0) {
					scrollTo = `stipendiary-employment-${idx}`;
				}

				return a?.positions?.length === 0;
			});
		const emptyNostip = values?.nonStipendiary?.filter((a, idx) => {
			if (!scrollTo && a?.positions?.length === 0) {
				scrollTo = `nonStipendiary-employment-${idx}`;
			}
			return a?.positions?.length === 0;
		});
		if (emptyNostip?.length > 0 || emptyStip.length > 0) {
			const scrollOptions: ScrollIntoViewOptions = {
				behavior: 'smooth',
				block: 'center',
			};

			const element = document.querySelector(`[data-formdata="${scrollTo}"]`);
			if (element) {
				if (element.previousElementSibling) {
					element.previousElementSibling.scrollIntoView(scrollOptions);
				} else {
					element.scrollIntoView(scrollOptions);
				}			
			}
			return;
		}
		dispatch(patchClergyBioMinistry(guid, ConstructPayload(values), onSuccess));

	};

	const addMinistries = () => {
		const currentMinistries = initialValues?.nonStipendiary;
		const newMinistries = currentMinistries.concat(createNewMinistry());
		const copy = { ...initialValues };
		copy.nonStipendiary = newMinistries;
		setInitialValues(copy);
	};

	const addPosition = (key: keyof ClergyBioMinistriesFormModel, idx: number, currentValues: any) => {
		const currentPositions = currentValues[key][idx]?.positions;
		const newPositions = currentPositions.concat(createNewPosition());
		const copy = _.cloneDeep(currentValues);
		copy[key][idx].positions = _.cloneDeep(newPositions);
		setInitialValues(copy);
	};


	const addMiscellaneous = () => {
		const currentMiscellaneous = initialValues?.miscellaneous;
		const newcurrentMiscellaneous = currentMiscellaneous.concat(createNewMiscellaneous());
		const copy = { ...initialValues };
		copy.miscellaneous = newcurrentMiscellaneous;
		setInitialValues(copy);
	};

	const validationSchema = GetClergyBioMinistriesValidationSchema(settings, labelSettings);


	useEffect(() => {
		setInitialValues({
			stipendiary: _.cloneDeep(ministries?.stipendiary?.details),
			miscellaneous: _.cloneDeep(ministries?.miscellaneous?.details),
			nonStipendiary: _.cloneDeep(ministries?.nonStipendiary?.details),
		});
	}, [ministries]);

	useEffect(() => {

		return function cleanUp() {
			dispatch(setLoadingState(DataState.LOADING, 'ministries'));
			dispatch(getClergyBioMinistry(
				guid,
				(data) => dispatch(setClergyBioMinistries(data?.data)),
				() => dispatch(setLoadingState(DataState.ERROR, 'ministries'))),
			);
		};
	}, []);

	function renderContent() {
		return (
			<Formik
				initialValues={initialValues}
				onSubmit={(values) => updateMinistries(values)}
				validationSchema={validationSchema}
				enableReinitialize
				initialErrors={apiErrors}
			>
				{(formProps: FormikProps<ClergyBioMinistriesFormModel>) => (

					<Form>
						<FormWrapper
							formikProps={formProps}
							submitText={labelSettings?.submitText}
							cancelText={labelSettings?.cancelButtonText}
							onCancel={() => {
								setUpdateStatus(ApiStatuses.None);
								setErrorMessage(null);
								dispatch(setLoadingState(DataState.LOADING, 'ministries'));
								dispatch(getClergyBioMinistry(
									guid,
									(data) => dispatch(setClergyBioMinistries(data?.data)),
									() => dispatch(setLoadingState(DataState.ERROR, 'ministries'))),
								);
								window.scrollTo(0, 0);
							}}
							initialValue={defaultValues}
							successfulForms={updateStatus === ApiStatuses.Success ? [FormTypes.CLERGY_BIO_MINISTRY_UPDATE] : []}
						>
							<PageHeader headerText={settings?.ministryTitle} />
							<ContentSpacer>
								<EcdMessage />
							</ContentSpacer>
							{
								updateStatus === ApiStatuses.Failure &&
								<ContentSpacer>
									<Banner
										banner={{
											type: 'error',
											label: <p><strong>{errorMessage}</strong></p>,
										}}
									/>
								</ContentSpacer>
							}
							<DividerSection sectionText={settings?.stipeTitle} sectionDescription={settings?.stipendiaryUpdateDescription} showCleryIcon>
								{
									!ministries?.stipendiary?.details || ministries?.stipendiary?.details?.length === 0 &&
									<ContentSpacer>
										<WysiwygWithReactLink content={settings?.noInformationOnFile} />
									</ContentSpacer>
								}
								{
									initialValues?.stipendiary?.map((s, idx) =>
										<ClergyBioMinistryItem
											key={`${s?.id}-${idx}`}
											settings={settings}
											id={idx?.toString()}
											arrayField='stipendiary'
											formikProps={formProps}
											employment={s}
											setList={setInitialValues}
											isStipe
											addPosition={addPosition}
											defaultValue={defaultValues?.stipendiary[idx]}
											errors={s?.errors}
											hideDivider={idx === initialValues?.stipendiary?.length - 1}
										/>)
								}
							</DividerSection>
							<DividerSection sectionText={settings?.nonStipeTitle} sectionDescription={settings?.nonStipendiaryUpdateDescription} showCleryIcon>
								{
									!ministries?.nonStipendiary?.details || ministries?.nonStipendiary?.details?.length === 0 &&
									<ContentSpacer>
										<WysiwygWithReactLink content={settings?.noInformationOnFile} />
									</ContentSpacer>
								}
								{
									initialValues?.nonStipendiary?.map((s, idx) =>
										<ClergyBioMinistryItem
											key={`${s?.id}-${idx}`}
											settings={settings}
											id={idx?.toString()}
											arrayField='nonStipendiary'
											formikProps={formProps}
											employment={s}
											setList={setInitialValues}
											addPosition={addPosition}
											defaultValue={defaultValues?.nonStipendiary[idx]}
											errors={s?.errors}
										/* hideDivider={idx === initialValues?.nonStipendiary?.length - 1}*/
										/>)
								}
								<button className={buttonClasses.primaryButton} type='button' onClick={() => addMinistries()} >
									<svg className='icon' aria-hidden='true' focusable='false'>
										<use xmlnsXlink='http://www.w3.org/2000/xlink' xlinkHref='#plus-icon' />
									</svg>
									{settings?.addNonStipendiaryButtonText}</button>
							</DividerSection>
							<DividerSection sectionText={settings?.miscellaneousTitle} sectionDescription={settings?.miscellaneousUpdateDescription} showCleryIcon>
								{
									!ministries?.miscellaneous?.details || ministries?.miscellaneous?.details?.length === 0 &&
									<ContentSpacer>
										<WysiwygWithReactLink content={settings?.noInformationOnFile} />
									</ContentSpacer>
								}
								{
									initialValues?.miscellaneous?.map((s, idx) =>
										<ClergyBioMiscellaneousItem
											key={`${s?.id}-${idx}`}
											settings={settings}
											id={idx?.toString()}
											arrayField='miscellaneous'
											formikProps={formProps}
											setList={setInitialValues}
											employment={s}
											defaultValue={defaultValues?.miscellaneous[idx]}
										/>)
								}
								<button className={buttonClasses.primaryButton} type='button' onClick={() => addMiscellaneous()} >
									<svg className='icon' aria-hidden='true' focusable='false'>
										<use xmlnsXlink='http://www.w3.org/2000/xlink' xlinkHref='#plus-icon' />
									</svg>
									{settings?.addMiscellaneousButtonText}</button>
							</DividerSection>
						</FormWrapper>
					</Form>

				)}
			</Formik>
		);
	}

	return (
		<ClergyBioComponentLoader
			state={loadingState}
			content={renderContent()}
		/>
	);
}


export default ClergyBioMinistriesUpdate;